import { useState, useEffect } from "react";
import { api, QUALIFICATION_LEVELS } from "../lib/utils";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  FunnelChart,
  Funnel,
  LabelList,
  AreaChart,
  Area,
} from "recharts";
import {
  Users,
  FileText,
  Building2,
  Briefcase,
  CheckCircle,
  Clock,
  AlertCircle,
  TrendingUp,
  Award,
  BookOpen,
  Target,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function Analytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await api.get("/analytics/dashboard");
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const levelData = analytics?.by_level
    ? Object.entries(analytics.by_level).map(([key, value]) => ({
        name: QUALIFICATION_LEVELS.find(l => l.value === key)?.label || key,
        count: value,
        fill: QUALIFICATION_LEVELS.find(l => l.value === key)?.value === 'doctoral' ? '#7C3AED' :
              QUALIFICATION_LEVELS.find(l => l.value === key)?.value === 'master' ? '#3B82F6' :
              QUALIFICATION_LEVELS.find(l => l.value === key)?.value === 'bachelor' ? '#10B981' : '#F59E0B'
      }))
    : [];

  const COLORS = {
    approved: "#10B981",
    rejected: "#EF4444", 
    pending: "#F59E0B",
    primary: "#1E3A8A",
    secondary: "#3B82F6",
    accent: "#8B5CF6",
  };

  const outcomeColors = ["#10B981", "#EF4444", "#F59E0B"];

  // Stats cards data
  const stats = [
    {
      title: "Total Candidates",
      value: analytics?.total_candidates || 0,
      icon: Users,
      color: "bg-blue-500",
      trend: "+12%",
      trendUp: true,
    },
    {
      title: "Documents",
      value: analytics?.total_documents || 0,
      icon: FileText,
      color: "bg-purple-500",
      subtitle: `${analytics?.avg_docs_per_candidate || 0} avg/candidate`,
    },
    {
      title: "Success Rate",
      value: `${analytics?.success_rate || 0}%`,
      icon: Target,
      color: "bg-emerald-500",
      subtitle: `${analytics?.approved_count || 0} approved`,
    },
    {
      title: "Awards",
      value: analytics?.total_awards || 0,
      icon: Award,
      color: "bg-amber-500",
    },
  ];

  const entityStats = [
    { title: "Institutions", value: analytics?.total_institutions || 0, icon: Building2 },
    { title: "IPCB Boards", value: analytics?.total_ipcbs || 0, icon: Briefcase },
    { title: "Competencies", value: analytics?.total_competencies || 0, icon: BookOpen },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
            <p className="text-slate-500 mt-1">Comprehensive overview of RPL assessment metrics</p>
          </div>
          <Button variant="outline" onClick={fetchAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Main Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`stat-card-${index}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-1">
                      {loading ? (
                        <span className="inline-block w-16 h-8 bg-slate-200 rounded animate-pulse"></span>
                      ) : stat.value}
                    </p>
                    {stat.subtitle && (
                      <p className="text-xs text-slate-400 mt-1">{stat.subtitle}</p>
                    )}
                    {stat.trend && (
                      <div className={`flex items-center mt-2 text-xs ${stat.trendUp ? 'text-emerald-600' : 'text-red-600'}`}>
                        {stat.trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        <span>{stat.trend} vs last month</span>
                      </div>
                    )}
                  </div>
                  <div className={`w-11 h-11 ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                    <stat.icon className="w-5 h-5 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Funnel Chart + Outcome Pie */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Workflow Funnel */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-blue-600" />
                Assessment Workflow Funnel
              </CardTitle>
              <CardDescription>Candidate progression through workflow stages</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-72 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : analytics?.funnel_data?.length > 0 ? (
                <div className="space-y-3">
                  {analytics.funnel_data.map((item, index) => (
                    <div key={index} className="relative">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">{item.stage}</span>
                        <span className="text-sm text-slate-500">{item.count} ({item.percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-8 overflow-hidden">
                        <div
                          className="h-8 rounded-full transition-all duration-500 flex items-center justify-end pr-3"
                          style={{
                            width: `${Math.max(item.percentage, 5)}%`,
                            background: `linear-gradient(90deg, ${
                              index === 0 ? '#3B82F6' :
                              index === 1 ? '#8B5CF6' :
                              index === 2 ? '#F59E0B' :
                              index === 3 ? '#10B981' :
                              index === 4 ? '#06B6D4' :
                              '#10B981'
                            } 0%, ${
                              index === 0 ? '#1D4ED8' :
                              index === 1 ? '#7C3AED' :
                              index === 2 ? '#D97706' :
                              index === 3 ? '#059669' :
                              index === 4 ? '#0891B2' :
                              '#059669'
                            } 100%)`
                          }}
                        >
                          <span className="text-white text-xs font-medium">{item.count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-72 flex items-center justify-center text-slate-500">
                  No workflow data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Outcome Distribution Pie */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Assessment Outcomes
              </CardTitle>
              <CardDescription>Distribution of final assessment decisions</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-72 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : analytics?.outcome_data?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={analytics.outcome_data}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {analytics.outcome_data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={outcomeColors[index]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span className="text-slate-600 text-sm">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-72 flex items-center justify-center text-slate-500">
                  No outcome data available
                </div>
              )}
              {/* Summary below pie */}
              {!loading && analytics && (
                <div className="grid grid-cols-3 gap-2 mt-2 pt-4 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-emerald-600">{analytics.approved_count || 0}</p>
                    <p className="text-xs text-slate-500">Approved</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{analytics.rejected_count || 0}</p>
                    <p className="text-xs text-slate-500">Rejected</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-500">{analytics.pending_count || 0}</p>
                    <p className="text-xs text-slate-500">In Progress</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend + Level Distribution */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Monthly Trend Line Chart */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Monthly Applications Trend
              </CardTitle>
              <CardDescription>Number of new candidates over time</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-72 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : analytics?.monthly_trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={analytics.monthly_trend}>
                    <defs>
                      <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="month" 
                      fontSize={12} 
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <YAxis 
                      fontSize={12} 
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                      formatter={(value) => [value, 'Candidates']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorCount)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-72 flex items-center justify-center text-slate-500">
                  No trend data available yet
                </div>
              )}
            </CardContent>
          </Card>

          {/* Level Distribution Bar Chart */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-600" />
                Candidates by Qualification Level
              </CardTitle>
              <CardDescription>Distribution across diploma, bachelor, master, doctoral</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-72 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : levelData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={levelData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                    <XAxis type="number" fontSize={12} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      fontSize={12} 
                      tickLine={false}
                      axisLine={{ stroke: '#e2e8f0' }}
                      width={100}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                      formatter={(value) => [value, 'Candidates']}
                    />
                    <Bar 
                      dataKey="count" 
                      radius={[0, 4, 4, 0]}
                      barSize={30}
                    >
                      {levelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-72 flex items-center justify-center text-slate-500">
                  No level data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{analytics?.approved_count || 0}</p>
                  <p className="text-xs text-emerald-600">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 bg-gradient-to-br from-amber-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold text-amber-700">
                    {(analytics?.by_status?.assessor_review || 0) + (analytics?.by_status?.iqa_review || 0)}
                  </p>
                  <p className="text-xs text-amber-600">In Review</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold text-purple-700">
                    {analytics?.by_status?.documents_uploaded || 0}
                  </p>
                  <p className="text-xs text-purple-600">Awaiting AI</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {entityStats.map((stat, index) => (
            <Card key={index} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <stat.icon className="w-8 h-8 text-slate-400" />
                  <div>
                    <p className="text-2xl font-bold text-slate-700">{loading ? '-' : stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Activity className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-800">Real-Time Analytics</h4>
                <p className="text-sm text-blue-700 mt-1">
                  This dashboard shows live data from your Credentia AI platform. The funnel chart tracks candidate progression, 
                  while the outcome pie shows final assessment decisions. Use these insights to identify bottlenecks and improve your RPL process.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
